// =============================================
// Notifications — Admin module
// =============================================

import { supabase } from '@/integrations/supabase/client';
import type { Notification, CreateNotificationParams } from '@/types/notifications';

// Batch stats type
export interface BatchStats {
  total_recipients: number;
  total_read: number;
  read_percentage: number;
  target_filter?: string;
}

// Admin: Fetch all notifications (for admin panel) - one representative per batch
export async function fetchAllNotifications(limit = 50): Promise<(Notification & { read_stats?: BatchStats | null })[]> {
  const { data: batchRows, error: batchError } = await supabase
    .from('notification_batch_stats' as any)
    .select('batch_id, total_recipients, total_read, read_percentage, created_at, target_filter')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (batchError) {
    console.error('Error fetching batch stats:', batchError);
    throw batchError;
  }

  const typedBatchRows = (batchRows || []) as unknown as Array<{
    batch_id: string;
    total_recipients: number;
    total_read: number;
    read_percentage: number;
    created_at: string;
    target_filter: string;
  }>;

  const batchIds = typedBatchRows.map(b => b.batch_id);

  const statsMap: Record<string, BatchStats> = {};
  for (const row of typedBatchRows) {
    statsMap[row.batch_id] = {
      total_recipients: row.total_recipients,
      total_read: row.total_read,
      read_percentage: row.read_percentage,
      target_filter: row.target_filter,
    };
  }

  let batchNotifications: Notification[] = [];
  if (batchIds.length > 0) {
    const repPromises = batchIds.map(batchId =>
      supabase
        .from('notifications')
        .select('*')
        .eq('batch_id', batchId)
        .eq('type', 'admin')
        .limit(1)
        .maybeSingle()
    );
    const results = await Promise.all(repPromises);
    for (const result of results) {
      if (result.data) {
        batchNotifications.push(result.data as Notification);
      }
    }
  }

  const { data: individualNotifs } = await supabase
    .from('notifications')
    .select('*')
    .eq('type', 'admin')
    .is('batch_id', null)
    .order('created_at', { ascending: false })
    .limit(limit);

  const allNotifs = [...batchNotifications, ...(individualNotifs as Notification[] || [])];
  allNotifs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const finalNotifs = allNotifs.slice(0, limit);

  const creatorIds = [...new Set(finalNotifs.filter(n => n.created_by).map(n => n.created_by as string))];
  let senderNameMap: Record<string, string> = {};

  if (creatorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('public_profiles' as any)
      .select('user_id, display_name')
      .in('user_id', creatorIds);

    if (profiles) {
      const typed = profiles as unknown as Array<{ user_id: string; display_name: string | null }>;
      senderNameMap = typed.reduce((acc, p) => {
        if (p.display_name) acc[p.user_id] = p.display_name;
        return acc;
      }, {} as Record<string, string>);
    }
  }

  const specificNotifs = finalNotifs.filter(n => {
    const tf = n.batch_id ? statsMap[n.batch_id]?.target_filter : null;
    return tf === 'specific';
  });
  let recipientNameMap: Record<string, string> = {};

  if (specificNotifs.length > 0) {
    const recipientIds = [...new Set(specificNotifs.map(n => n.user_id))];
    const { data: recipientProfiles } = await supabase
      .from('public_profiles' as any)
      .select('user_id, display_name')
      .in('user_id', recipientIds);

    if (recipientProfiles) {
      const typed = recipientProfiles as unknown as Array<{ user_id: string; display_name: string | null }>;
      recipientNameMap = typed.reduce((acc, p) => {
        if (p.display_name) acc[p.user_id] = p.display_name;
        return acc;
      }, {} as Record<string, string>);
    }
  }

  return finalNotifs.map(n => {
    const targetFilter = n.batch_id ? statsMap[n.batch_id]?.target_filter : null;
    return {
      ...n,
      read_stats: n.batch_id ? statsMap[n.batch_id] ?? null : null,
      sender_name: n.created_by ? senderNameMap[n.created_by] ?? null : null,
      recipient_name: targetFilter === 'specific' ? recipientNameMap[n.user_id] ?? null : null,
    };
  });
}

// Admin: Create notification for multiple users (with batching for large sets)
export async function createBulkNotifications(
  userIds: string[],
  params: Omit<CreateNotificationParams, 'user_id'>,
  targetFilter: string = 'all'
): Promise<void> {
  // Server-side delivery via Edge Function — guarantees completion even if the
  // admin closes their browser tab. Replaces the previous client-side loop that
  // could drop the final batch on tab visibility changes.
  const { data, error } = await supabase.functions.invoke('bulk-notifications', {
    body: { userIds, params, targetFilter },
  });

  if (error) {
    console.error('Error invoking bulk-notifications:', error);
    throw error;
  }

  if (data?.failed && data.failed > 0) {
    console.warn(`bulk-notifications: ${data.failed}/${data.total} failed in batch ${data.batch_id}`);
  }
}

// Refresh batch stats manually for a specific batch
export async function refreshBatchStats(batchId: string): Promise<number> {
  const { data, error } = await supabase.rpc('recalculate_batch_stats', { 
    p_batch_id: batchId 
  });

  if (error) {
    console.error('Error refreshing batch stats:', error);
    throw error;
  }

  return data as number;
}

// Fetch batch stats for view rate display
export async function fetchBatchStats(batchIds: string[]): Promise<Record<string, BatchStats>> {
  if (batchIds.length === 0) return {};

  const { data, error } = await supabase
    .from('notification_batch_stats' as any)
    .select('batch_id, total_recipients, total_read, read_percentage')
    .in('batch_id', batchIds);

  if (error) {
    console.error('Error fetching batch stats:', error);
    return {};
  }

  if (!data) return {};

  return (data as unknown as Array<{
    batch_id: string;
    total_recipients: number;
    total_read: number;
    read_percentage: number;
  }>).reduce((acc, row) => {
    acc[row.batch_id] = {
      total_recipients: row.total_recipients,
      total_read: row.total_read,
      read_percentage: row.read_percentage,
    };
    return acc;
  }, {} as Record<string, BatchStats>);
}

// Admin: Fetch all user IDs for broadcast (with pagination to bypass 1000 limit)
export async function fetchAllUserIds(): Promise<string[]> {
  const PAGE_SIZE = 1000;
  const allUserIds: string[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id')
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      console.error('Error fetching user IDs:', error);
      throw error;
    }

    if (data && data.length > 0) {
      allUserIds.push(...data.map(p => p.user_id));
      offset += PAGE_SIZE;
      hasMore = data.length === PAGE_SIZE;
    } else {
      hasMore = false;
    }
  }

  return allUserIds;
}

// Helper function for paginated queries
async function fetchPaginatedUserIds(
  queryFn: (offset: number, pageSize: number) => PromiseLike<{ data: { user_id: string }[] | null; error: any }>
): Promise<string[]> {
  const PAGE_SIZE = 1000;
  const allUserIds: string[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await queryFn(offset, PAGE_SIZE);

    if (error) {
      throw error;
    }

    if (data && data.length > 0) {
      allUserIds.push(...data.map(p => p.user_id));
      offset += PAGE_SIZE;
      hasMore = data.length === PAGE_SIZE;
    } else {
      hasMore = false;
    }
  }

  return allUserIds;
}

// Admin: Fetch user IDs by filter (with pagination)
export async function fetchUserIdsByFilter(filter: string): Promise<string[]> {
  switch (filter) {
    case 'all':
      return fetchAllUserIds();

    case 'sellers': {
      try {
        return await fetchPaginatedUserIds((offset, pageSize) =>
          supabase
            .from('profiles')
            .select('user_id')
            .not('seller_type_id', 'is', null)
            .range(offset, offset + pageSize - 1)
        );
      } catch (error) {
        console.error('Error fetching sellers:', error);
        throw error;
      }
    }

    case 'seller_apprentice':
    case 'seller_master': {
      const { data: sellerType } = await supabase
        .from('seller_types')
        .select('id')
        .eq('name', filter)
        .maybeSingle();

      if (!sellerType) return [];

      try {
        return await fetchPaginatedUserIds((offset, pageSize) =>
          supabase
            .from('profiles')
            .select('user_id')
            .eq('seller_type_id', sellerType.id)
            .range(offset, offset + pageSize - 1)
        );
      } catch (error) {
        console.error(`Error fetching ${filter}:`, error);
        throw error;
      }
    }

    case 'admins': {
      try {
        return await fetchPaginatedUserIds((offset, pageSize) =>
          supabase
            .from('user_roles')
            .select('user_id')
            .in('role', ['admin', 'staff'])
            .range(offset, offset + pageSize - 1)
        );
      } catch (error) {
        console.error('Error fetching admins:', error);
        throw error;
      }
    }

    case 'students':
    case 'visitors':
    case 'partners': {
      const roleMap = {
        students: 'student' as const,
        visitors: 'visitor' as const,
        partners: 'partner' as const,
      };
      try {
        // Fetch target role users
        const roleUsers = await fetchPaginatedUserIds((offset, pageSize) =>
          supabase
            .from('user_roles')
            .select('user_id')
            .eq('role', roleMap[filter])
            .range(offset, offset + pageSize - 1)
        );

        // Also include admin/staff so they can follow along
        const adminUsers = await fetchPaginatedUserIds((offset, pageSize) =>
          supabase
            .from('user_roles')
            .select('user_id')
            .in('role', ['admin', 'staff'])
            .range(offset, offset + pageSize - 1)
        );

        // Merge and deduplicate
        const uniqueIds = [...new Set([...roleUsers, ...adminUsers])];
        return uniqueIds;
      } catch (error) {
        console.error(`Error fetching ${filter}:`, error);
        throw error;
      }
    }

    default:
      return [];
  }
}

// Admin: Search users by name or email
export interface SearchedUser {
  user_id: string;
  display_name: string | null;
  email?: string;
}

export async function searchUsers(query: string): Promise<SearchedUser[]> {
  if (!query || query.length < 2) return [];

  const { data, error } = await supabase
    .rpc('search_users_for_notification', { search_query: query });

  if (error) {
    console.error('Error searching users:', error);
    throw error;
  }

  return (data as SearchedUser[]) || [];
}

// Upload notification attachment (with compression for images)
export async function uploadNotificationAttachment(file: File): Promise<string> {
  let uploadBlob: Blob = file;
  let fileName: string;
  let contentType: string | undefined;

  if (file.type.startsWith('image/')) {
    const { prepareImageForUpload } = await import('@/lib/imageUtils');
    const { blob, extension, mimeType } = await prepareImageForUpload(file, 'notificationAttachment');
    uploadBlob = blob;
    fileName = `${crypto.randomUUID()}.${extension}`;
    contentType = mimeType;
  } else {
    const fileExt = file.name.split('.').pop();
    fileName = `${crypto.randomUUID()}.${fileExt}`;
  }

  const { error: uploadError } = await supabase.storage
    .from('notification-attachments')
    .upload(fileName, uploadBlob, {
      contentType
    });

  if (uploadError) {
    console.error('Error uploading attachment:', uploadError);
    throw uploadError;
  }

  const { data } = supabase.storage
    .from('notification-attachments')
    .getPublicUrl(fileName);

  return data.publicUrl;
}

// Delete notification (admin only)
export async function deleteNotification(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
}

// Delete all notifications in a batch (admin only)
export async function deleteNotificationBatch(batchId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('batch_id', batchId);

  if (error) {
    console.error('Error deleting notification batch:', error);
    throw error;
  }
}

// Delete single notification OR entire batch
export async function deleteNotificationWithBatch(
  notificationId: string,
  batchId: string | null | undefined
): Promise<void> {
  if (batchId) {
    await deleteNotificationBatch(batchId);
  } else {
    const { data: notification } = await supabase
      .from('notifications')
      .select('title, created_at')
      .eq('id', notificationId)
      .single();

    if (notification) {
      const createdAt = new Date(notification.created_at);
      const minTime = new Date(createdAt.getTime() - 1000).toISOString();
      const maxTime = new Date(createdAt.getTime() + 1000).toISOString();

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('title', notification.title)
        .gte('created_at', minTime)
        .lte('created_at', maxTime);

      if (error) throw error;
    } else {
      await deleteNotification(notificationId);
    }
  }
}

// Admin: Cleanup old read notifications (30+ days old)
export async function cleanupOldNotifications(): Promise<number> {
  const { data, error } = await supabase.rpc('cleanup_old_notifications');

  if (error) {
    console.error('Error cleaning up old notifications:', error);
    throw error;
  }

  return data as number;
}
